import { createContext, useContext, useState } from "react";
import { TipoDocumento } from "@/interfaces";
import axiosGenerico from "../../../utils/axiosGenerico";
import { useCargando } from "../../../context/cargando-context";
import { useAuth } from "../../../context/auth-context";
import { BotonDescarga } from "../../../components/ButtonDownload";
import { useSnackbar } from "notistack";

type DocumentsContextType = {
  listaTipoDocumento: TipoDocumento[];
  rows: any[];
  formulario: any;
  setListaTipoDocumento: (value: TipoDocumento[]) => void;
  setFormulario: (value: any) => void;
  cargarTipoDocumento: () => void;
  buscar: () => void;
  limpiar: () => void;
  columns: any[];
};

const DocumentsContext = createContext<DocumentsContextType>(null!);

const DocumentsProvider = ({ children }: { children: React.ReactNode }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { empezarCarga, terminarCarga } = useCargando();
  const { cliente } = useAuth();
  const [listaTipoDocumento, setListaTipoDocumento] = useState<TipoDocumento[]>(
    []
  );
  const [rows, setRows] = useState<any[]>([]);
  const [formulario, setFormulario] = useState({
    fechaDesde: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    fechaHasta: new Date(),
    tipoDocumento: "",
  });

  const cargarTipoDocumento = async () => {
    await axiosGenerico
      .get("/documentos/tipos")
      .then((response) => {
        const datos = response.data.map((item: any) => ({
          codigo: item.codigodoc,
          nombre: item.nombredoc,
        }));
        setListaTipoDocumento(datos);
        setFormulario((last) => ({
          ...last,
          tipoDocumento: datos[0]?.codigo,
        }));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const buscar = async () => {
    try {
      empezarCarga();
      const { fechaDesde, fechaHasta, tipoDocumento } = formulario;
      const form = {
        tipoDocumento,
        cliente: cliente.codigo,
        fechaHasta: fechaHasta.toISOString(),
        fechaDesde: fechaDesde.toISOString(),
      };

      await axiosGenerico
        .get("/documentos/listar", { params: form })
        .then((res) => {
          if (res.data.length === 0) {
            enqueueSnackbar("No se encontraron resultados", {
              variant: "info",
            });
            return;
          }
          setRows(res.data.map((item: any, i) => ({ ...item, id: i + 1 })));
        });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      terminarCarga();
    }
  };

  const limpiar = () => {
    setFormulario({
      fechaDesde: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      fechaHasta: new Date(),
      tipoDocumento: "",
    });

    setRows([]);
  };

  const columns = [
    {
      field: "xml",
      headerName: "XML",
      align: "center",
      width: 120,
      renderCell: (param: any) => (
        <BotonDescarga
          url="/documentos/descargar-xml"
          nombreArchivo={`${param.row.claveAcceso}`}
          titulo="Descargar"
          getParams={() => ({
            nombreArchivo: param.row.nombreDocumento,
          })}
          propiedades={{
            variant: "outlined",
          }}
        />
      ),
    },
    {
      field: "pdf",
      headerName: "PDF",
      align: "center",
      width: 120,
      renderCell: (param: any) => (
        <BotonDescarga
          url="/documentos/descargar-pdf"
          nombreArchivo={`${param.row.claveAcceso}`}
          titulo="Descargar"
          getParams={() => ({
            nombreArchivo: param.row.nombreDocumento,
          })}
          propiedades={{
            variant: "outlined",
          }}
        />
      ),
    },
    { field: "nombreTipoDocumento", headerName: "Tipo", width: 150 },
    { field: "nombreDocumento", headerName: "Nombre Doc.", width: 200 },
    {
      field: "fechaEmision",
      headerName: "Fecha Emi.",
      width: 120,
      valueFormatter: (params) =>
        params ? new Date(params).toLocaleDateString("es-ES") : "",
    },
    { field: "claveAcceso", headerName: "Clave Acceso", width: 450 },
  ];

  return (
    <DocumentsContext.Provider
      value={{
        listaTipoDocumento,
        rows,
        formulario,
        setListaTipoDocumento,
        setFormulario,
        cargarTipoDocumento,
        buscar,
        limpiar,
        columns,
      }}
    >
      {children}
    </DocumentsContext.Provider>
  );
};

export { DocumentsContext, DocumentsProvider };

export const useDocuments = () => useContext(DocumentsContext);
